import { API_BASE_URL } from "./api-base";
import { db, type AuthRecord } from "./dexie-db";
import { parseJWTClaims } from "./jwt-claims";

/**
 * Lightweight fetch-wrapper für die JSON-API.
 *
 *  - Setzt Authorization-header aus dexie-auth-row (wenn vorhanden)
 *  - Proaktiver silent-refresh: wenn accessTokenExpiresAt < 2min, refresh VOR dem request
 *  - 401 → versucht einmal refresh (single-flight: parallele 401s teilen einen refresh-call)
 *  - Refresh fehlgeschlagen → clearAuth + sessionStorage-flag + Event → logout
 *  - Bei network-fail (offline) wirft NetworkError — outbox-pattern in
 *    den feature-mutations entscheidet ob das in outbox geht
 *  - Bei response.ok=false: parsed JSON-error-envelope ({error: "code"})
 *    → typed APIError. Unbekannte fehler → APIError mit message=raw-text.
 */

export class APIError extends Error {
  readonly status: number;
  readonly code: string;
  readonly retryAfter?: number;

  constructor(status: number, code: string, message?: string, retryAfter?: number) {
    super(message ?? code);
    this.name = "APIError";
    this.status = status;
    this.code = code;
    this.retryAfter = retryAfter;
  }
}

export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("network unreachable");
    this.name = "NetworkError";
    this.cause = cause;
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Skip auth-header (für /auth/magic-link, /auth/verify) */
  anonymous?: boolean;
  /** Bei 401 NICHT versuchen zu refresh-en (vermeidet rekursion in refresh-call selbst) */
  noRefresh?: boolean;
}

async function loadAuth(): Promise<AuthRecord | undefined> {
  return await db.auth.get("current");
}

async function saveAuth(record: AuthRecord): Promise<void> {
  await db.auth.put(record);
}

async function clearAuth(): Promise<void> {
  await db.auth.delete("current");
}

interface RefreshResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

/**
 * Single-flight-promise für laufende Refresh-Requests.
 * Parallele 401-Caller teilen denselben promise — nur ein
 * Refresh-Call pro Zeitpunkt, verhindert Token-Family-Revoke durch Race.
 */
let _refreshInFlight: Promise<AuthRecord | undefined> | null = null;

function tryRefresh(): Promise<AuthRecord | undefined> {
  if (_refreshInFlight) return _refreshInFlight;

  const p: Promise<AuthRecord | undefined> = (async () => {
    const current = await loadAuth();
    if (!current) return undefined;
    try {
      const resp = await rawFetch<RefreshResponse>("/auth/refresh", {
        method: "POST",
        body: { refresh_token: current.refreshToken },
        anonymous: true,
      });
      // Phase 18.E — refresh-rotation kann einen neuen lic_exp tragen
      // (z.B. nach trial-extension oder zahlungs-eingang). Wir re-parsen
      // ihn aus dem access-token statt den stale dexie-wert zu behalten.
      const claims = parseJWTClaims(resp.access_token);
      const next: AuthRecord = {
        ...current,
        accessToken: resp.access_token,
        refreshToken: resp.refresh_token,
        accessTokenExpiresAt: Date.now() + resp.expires_in * 1000,
        user: {
          ...current.user,
          licenseExpiresAt: claims.lic_exp,
        },
      };
      await saveAuth(next);
      return next;
    } catch {
      return undefined;
    } finally {
      _refreshInFlight = null;
    }
  })();

  _refreshInFlight = p;
  return p;
}

/**
 * Proaktiver silent-refresh: wird von rawFetch aufgerufen wenn
 * accessTokenExpiresAt < 2 Minuten entfernt ist. Fire-and-forget
 * im Erfolgsfall (token wird in dexie geschrieben, nächster request
 * liest es frisch via loadAuth). Bei Fehler: ignorieren — 401-Fallback greift.
 */
async function proactiveRefreshIfNeeded(): Promise<void> {
  const auth = await loadAuth();
  if (!auth) return;
  const twoMinMs = 2 * 60 * 1000;
  if (auth.accessTokenExpiresAt - Date.now() < twoMinMs) {
    await tryRefresh();
  }
}

async function rawFetch<T>(path: string, opts: RequestOptions): Promise<T> {
  const url = path.startsWith("http") ? path : `${API_BASE_URL}${path}`;
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...opts.headers,
  };
  if (opts.body !== undefined && opts.body !== null) {
    headers["Content-Type"] = "application/json";
  }
  if (!opts.anonymous) {
    // Proaktiver refresh bevor das token gelesen wird — stellt sicher dass
    // der Authorization-header immer ein frisches token trägt.
    if (!opts.noRefresh) {
      await proactiveRefreshIfNeeded();
    }
    const auth = await loadAuth();
    if (auth) {
      headers["Authorization"] = `Bearer ${auth.accessToken}`;
    }
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method: opts.method ?? "GET",
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      credentials: "omit",
      signal: opts.signal,
    });
  } catch (err) {
    throw new NetworkError(err);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  if (!response.ok) {
    let code = `http_${response.status}`;
    let message = `HTTP ${response.status}`;
    let retryAfterSec: number | undefined;
    try {
      const body = (await response.clone().json()) as { error?: string };
      if (body.error) {
        code = body.error;
        message = body.error;
      }
    } catch {
      try {
        message = await response.text();
      } catch {
        // empty body, keep defaults
      }
    }
    const retryHeader = response.headers.get("Retry-After");
    if (retryHeader) {
      const parsed = parseInt(retryHeader, 10);
      if (!Number.isNaN(parsed)) retryAfterSec = parsed;
    }
    throw new APIError(response.status, code, message, retryAfterSec);
  }

  try {
    return (await response.json()) as T;
  } catch (err) {
    throw new APIError(response.status, "invalid_json", String(err));
  }
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  try {
    return await rawFetch<T>(path, opts);
  } catch (err) {
    if (err instanceof APIError && err.status === 401 && !opts.noRefresh) {
      const refreshed = await tryRefresh();
      if (refreshed) {
        return await rawFetch<T>(path, { ...opts, noRefresh: true });
      }
      // refresh failed → drop auth + signal logout to the UI via a
      // browser-event the App.tsx listens for.
      // sessionStorage-flag damit login-route die abgelaufene-session-variante zeigt.
      try {
        sessionStorage.setItem("hp:logout_reason", "expired");
      } catch {
        // sessionStorage kann in private-mode oder bei storage-quota blockiert sein — ignorieren
      }
      await clearAuth();
      window.dispatchEvent(
        new CustomEvent("hitzeprotokoll:logout", { detail: { reason: "expired" } }),
      );
    }
    throw err;
  }
}

export { loadAuth, saveAuth, clearAuth };
